import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../contexts/DependencyProvider";
import EventInfo from "./FormComponents/EventInfo";
import {
  ActionButton,
  Checkbox,
  ComboBox,
  Content,
  DatePicker,
  Divider,
  Flex,
  Item,
  ProgressCircle,
  Text,
  TextField,
  ToggleButton,
  View,
  Dialog,
  Heading,
  ToastQueue,
  DialogContainer
} from "@adobe/react-spectrum";
import { useEffect, useState } from "react";
import styles from "./add_update_event.module.css";
import { Button } from "react-bootstrap";
import {
  ArrowLeftLongIcon,
  ArrowRightLongIcon,
  CloseIcon
} from "../../../../components/protected/Icons/IconsLib";
import React from "react";
import TinyMCEEditor from "../../../../components/ui/TinyMCEEditor/TinyMCEEditior";
import { EventItem } from "../../../../services/soap/dataObjects/EventItem";
import { CreateEventRequest } from "../../../../services/soap/scheduler/requests/CreateEventRequest";
import { DateValue, fromDate, getLocalTimeZone } from "@internationalized/date";
import AddUpdateRecurringEvent from "./FormComponents/EventRecurring";
import { UpdateEventRequest } from "../../../../services/soap/scheduler/requests/UpdateEventRequest";
import moment from "moment";
import { Star } from "react-bootstrap-icons";
import { fromBase64Unicode, toBase64Unicode } from "../../../../utils/helperUtil";

interface IAddupdateeventProps {
  event: any;
  isEdit: boolean;
  onClose: () => void;
  refreshDetailView: (data: any) => void;
  handleRefreshAddNewEvent: (data: any) => void;
  typeOfWorkList?: any;
  eventStatusList?: any;
  userList?: any;
  context?: any;
}

const addDays = (d: Date, days: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

type SelectedUser = { id: string; name: string; isHead: boolean };

const AddUpdateEvent: React.FC<IAddupdateeventProps> = ({
  event,
  isEdit,
  onClose,
  refreshDetailView,
  handleRefreshAddNewEvent,
  typeOfWorkList,
  eventStatusList,
  userList,
  context
}) => {
  const { t } = useTranslation("calendar");
  const _t = (key: string) => t(`add_update_event.${key}`, { ns: "calendar" });
  const _tt = (key: string) => t(`calendar_common.${key}`, { ns: "calendar" });
  const _tm = (key: string) => t(`calendar_messages.${key}`, { ns: "calendar" });

  const { store, schedulerService } = useDependency();

  const [showLoader, setShowLoader] = useState(false);
  const [statusItems, setStatusItems] = useState(
    eventStatusList.filter((s: any) => s.title !== "") || []
  );
  const [formData, setFormData] = useState<any>({});
  const [subjectError, setsubjectError] = useState<string | null>(null);
  const [typeError, setTypeError] = useState<string | null>(null);
  const [priorityError, setPriorityError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [dateFromValue, setDateFromValue] = useState<DateValue | null>(null);
  const [dateToValue, setDateToValue] = useState<DateValue | null>(null);
  const [dateFromError, setDateFromError] = useState<string | null>(null);
  const [dateToError, setDateToError] = useState<string | null>(null);

  // ✅ USERS MODEL (auto-add on selection, no add button)
  const [userPickerKey, setUserPickerKey] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);

  const [repeat, setRepeat] = useState<any | null>({
    repeatMode: "2",
    validFrom: moment().format("YYYY-MM-DD"),
    validTo: moment(addDays(new Date(), 30)).format("YYYY-MM-DD"),
    repeatEvery: "1",
    repeatDay: "0",
    repeatMonth: "0",
    repeatWeekDay: "0",
    repeatWeekCount: "0",
    repeatWeekDayList: "",
    timeFrom: "08:00",
    timeTo: "09:00",
    offset: "0",
    limit: "1"
  });
  const [isMultiEvent, setIsMultiEvent] = useState("0");
  const [showRecurrence, setShowRecurrence] = useState(false);
  const [enableReminder, setEnableReminder] = useState(false);
  const [enableRecurrence, setEnableRecurrence] = useState(false);

  const REMINDER_MINUTE_VALUES: number[] = [
    0, 5, 10, 15, 20, 30, 60, 120, 180, 240, 300, 360, 420, 480, 540, 600, 660,
    720, 1080, 1440, 2880, 4320, 5760, 10080, 20160
  ];

  function formatReminderLabel(minutes: number): string {
    if (minutes === 0) return "0 minutes";

    const MINUTES_PER_DAY = 24 * 60;
    const MINUTES_PER_HOUR = 60;

    if (minutes % MINUTES_PER_DAY === 0) {
      const days = minutes / MINUTES_PER_DAY;
      if (days % 7 === 0) {
        const weeks = days / 7;
        return weeks === 1 ? "1 week" : `${weeks} weeks`;
      }
      return days === 1 ? "1 day" : `${days} days`;
    }

    if (minutes % MINUTES_PER_HOUR === 0) {
      const hours = minutes / MINUTES_PER_HOUR;
      return hours === 1 ? "1 hour" : `${hours} hours`;
    }

    return minutes === 1 ? "1 minute" : `${minutes} minutes`;
  }

  const BASE_REMINDER_ITEMS = REMINDER_MINUTE_VALUES.map((m) => ({
    key: m.toString(),
    minutes: m
  }));

  const availableUsers = (userList ?? []).filter(
    (u: any) => !selectedUsers.some((su) => su.id === String(u.id))
  );


  // useEffect(() => {
  //   if (availableUsers.length > 0) {
  //     setUserPickerKey(String(availableUsers[0].id));
  //   } else {
  //     setUserPickerKey(null);
  //   }
  // }, [selectedUsers]);

  // ✅ USERS: add by id (auto-add on selection)
  const addUserById = (id: string) => {
    const u = (userList ?? []).find((x: any) => String(x.id) === String(id));
    if (!u) return;

    setSelectedUsers((prev) => {
      const exists = prev.some((p) => p.id === String(u.id));
      if (exists) return prev;

      return [
        ...prev,
        { id: String(u.id), name: u.name, isHead: prev.length === 0 } // first becomes head
      ];
    });
  };

  const handleRemoveSelectedUser = (id: string) => {
    setSelectedUsers((prev) => {
      const next = prev.filter((u) => u.id !== id);

      // if head removed, auto-assign head to first remaining user
      if (next.length && !next.some((u) => u.isHead)) {
        next[0] = { ...next[0], isHead: true };
      }
      return next;
    });
  };

  const handleSetHead = (id: string) => {
    setSelectedUsers((prev) => prev.map((u) => ({ ...u, isHead: u.id === id })));
  };

  // ✅ Sync to formData: userIds + headUserId
  useEffect(() => {
    const ids = selectedUsers.map((u) => u.id);
    const head =
      selectedUsers.find((u) => u.isHead)?.id ?? store.UserId.toString();

    setFormData((prev: any) => ({
      ...prev,
      userIds: ids,
      headUserId: head
    }));
  }, [selectedUsers, store.UserId]);

  useEffect(() => {
    if (isEdit && event) {
      const tz = getLocalTimeZone();

      setDateFromValue(event.dateFrom ? fromDate(new Date(event.dateFrom), tz) : null);
      setDateToValue(event.dateTo ? fromDate(new Date(event.dateTo), tz) : null);

      // ✅ init selected users for edit
      const initialUsers: SelectedUser[] = Array.isArray(event?.users)
        ? event.users.map((g: any, idx: number) => {
          const id = String(g.id);
          const name =
            g.name ??
            (userList ?? []).find((u: any) => String(u.id) === id)?.name ??
            id;

          const isHead =
            String(event.headUserId) === id || (!event.headUserId && idx === 0);

          return { id, name, isHead };
        })
        : [];

      setSelectedUsers(initialUsers);
      setEnableRecurrence(event.isRepeat)
      setRepeat(event.repeat);
      setFormData({
        ...event,
        reminder: +event.reminder,
        description: fromBase64Unicode(event.description)
      });
    } else {
      const tz = getLocalTimeZone();
      const now = new Date();
      const inThirtyMinutes = new Date(now.getTime() + 30 * 60 * 1000);
      setDateFromValue(fromDate(now, tz));
      setDateToValue(fromDate(inThirtyMinutes, tz));

      setSelectedUsers([]);
      const defaultTypeOfWork = typeOfWorkList.length ? typeOfWorkList.find((x: any) => +x.defaultFor === 1) : '';
      setFormData({
        solutionStatus: statusItems[0]?.ident || "",
        title: "",
        location: "",
        comments: "",
        description:"",
        isRepeat: false,
        isPrivate: false,
        isChargeable: false,
        entryType: defaultTypeOfWork ? defaultTypeOfWork.ident : '',
        reminder: null,
        userIds: [],
        headUserId: store.UserId.toString(),
        activityId: context?.activityId || '',
        projectId: context?.projectId || '',
        folderId: context?.folderId || '',
      });
    }
  }, [event, isEdit, userList, statusItems, typeOfWorkList, store.UserId]);

  const getTimeFromDateValue = (dateValue?: any) => {
    if (!dateValue) return null;
    const tz = getLocalTimeZone();
    const jsDate = dateValue.toDate(tz);

    return jsDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  };

  // const onSubmit = async () => {
  const onSubmit = async () => {
  // Clear previous errors
  setsubjectError("");
  setTypeError("");
  setStatusError("");
  setDateFromError("");
  setDateToError("");

  const subject = formData.title?.trim();
  const entryType = formData.entryType?.trim();
  const solutionStatus = formData.solutionStatus?.trim();

  const missing: string[] = [];

  if (!subject) {
    setsubjectError(_tm("subject_is_required"));
    missing.push("subject");
  }
  if (!entryType) {
    setTypeError(_tm("type_is_required"));
    missing.push("type");
  }
  if (!solutionStatus) {
    setStatusError(_tm("status_is_required"));
    missing.push("status");
  }
  if (!dateFromValue) {
    setDateFromError(_tm("start_date_is_required"));
    missing.push("dateFrom");
  }
  if (!dateToValue) {
    setDateToError(_tm("end_date_is_required"));
    missing.push("dateTo");
  }

  if (missing.length) {
    ToastQueue.negative(`Please fill in: ${missing.join(", ")}.`, { timeout: 3000 });
    return;
  }

  // Interpret picker values as LOCAL dates (based on local timezone)
  const tz = getLocalTimeZone();
  const fromLocalDate = dateFromValue!.toDate(tz);
  const toLocalDate = dateToValue!.toDate(tz);

  // Validate using local time (what user expects)
  if (toLocalDate < fromLocalDate) {
    setDateToError("End date must be after start date");
    ToastQueue.negative("End date must be after start date.", { timeout: 3000 });
    return;
  }

  // Save as LOCAL datetime string (NO UTC conversion, NO Z)
  // Format: 2025-12-29T16:26:07
  const fromLocal = moment(fromLocalDate).format("YYYY-MM-DDTHH:mm:ss");
  const toLocal = moment(toLocalDate).format("YYYY-MM-DDTHH:mm:ss");

  try {
    const requestPayload: EventItem = {
      userId: store.UserId.toString(),
      forUser: store.UserId.toString(),
      location: formData.location,
      description: toBase64Unicode(formData.description),
      title: subject!,
      entryType: entryType!,
      solutionStatus: solutionStatus!,
      headUserId: (formData.headUserId ?? store.UserId.toString()).toString(),
      REPEAT: repeat,
      isPrivate: formData.isPrivate === true ? "1" : "0",
      chargeable: formData.isChargeable === true ? "1" : "0",
      reminder: formData.reminder,
      dateFrom: fromLocal,
      dateTo: toLocal,
      projectId: formData.projectId,
      folderId: formData.folderId,
      activityId: formData.activityId,
      USER:
        formData.userIds && formData.userIds.length > 0
          ? (formData.userIds ?? []).map((userId: any) => ({ id: userId }))
          : [{ id: store.UserId }],
      multiEventTask: isMultiEvent
    };

    setShowLoader(true);

    if (!isEdit) {
      const addRes: any = await schedulerService.createEvent(
        new CreateEventRequest(store.Server, store.SessionId, requestPayload)
      );

      if (addRes?.result === "OK") {
        ToastQueue.positive("Event created successfully", { timeout: 3000 });
        refreshDetailView(addRes.newId);
        onClose();
      } else {
        ToastQueue.negative("Error creating event", { timeout: 3000 });
      }
    } else {
      const { result, EXCEPTION } = await schedulerService.updateEvent(
        new UpdateEventRequest(store.Server, store.SessionId, {
          ...event,
          ...requestPayload
        })
      );

      if (result === "OK") {
        ToastQueue.positive("Event updated successfully", { timeout: 3000 });
        refreshDetailView && refreshDetailView({ ...event, ...requestPayload });
        onClose && onClose();
      } else {
        ToastQueue.negative(EXCEPTION?.message || "Error in submitting form data", { timeout: 3000 });
        console.error("Error in submitting form data:", EXCEPTION?.message);
      }
    }
  } catch (err) {
    console.error("Error submitting form data", err);
    ToastQueue.negative("Unexpected error while saving event", { timeout: 3000 });
  } finally {
    setShowLoader(false);
  }
};

  return (
    <>
      <Dialog
        width={{ base: "100%", M: "100%" }}
        height={{ base: "100%", M: "100%" }}
        UNSAFE_style={{ background: "#fff" }}
        isDismissable
        onDismiss={onClose}
        aria-label={isEdit ? _t("edit_event") : _t("add_event")}
      >
        <Heading>{isEdit ? _t("edit_event") : _t("add_event")}</Heading>

        <Content>
          <Flex direction="column" gap="size-100">
            <View>
              <Flex direction={"row"} marginTop={16} alignItems={"center"} gap={8}>
                <TextField
                  label={_t("subject")}
                  aria-label="Subject"
                  labelPosition="side"
                  isRequired
                  value={formData.title || ""}
                  onChange={(e) => {
                    if (subjectError) setsubjectError(null);
                    setFormData({ ...formData, title: e });
                  }}
                  width="100%"
                  marginTop={8}
                  UNSAFE_className="no-bottom-border-input"
                  isQuiet
                  validationState={subjectError ? "invalid" : undefined}
                  errorMessage={subjectError || undefined}
                />
              </Flex>

              <Divider size="M" UNSAFE_style={{ marginTop: "5px" }} />

              <Flex
                direction={{ base: "column", L: "row" }}
                marginTop={16}
                alignItems={"center"}
                gap={8}
              >
                <TextField
                  label={_t("context")}
                  labelPosition="side"
                  aria-label="Context"
                  width={{ base: "100%", L: "60%" }}
                  isQuiet
                  UNSAFE_className="no-bottom-border-input"
                />
                <ComboBox
                  label={_t("type")}
                  labelPosition="side"
                  aria-label="Type"
                  isRequired
                  isQuiet
                  UNSAFE_className="no-bottom-border-input"
                  items={typeOfWorkList}
                  width={{ base: "100%", L: "40%" }}
                  selectedKey={formData.entryType}
                  onSelectionChange={(e) => {
                    if (typeError) setTypeError(null);
                    setFormData({ ...formData, entryType: e?.toString() || "" });
                  }}
                  validationState={typeError ? "invalid" : undefined}
                  errorMessage={typeError || undefined}
                >
                  {(item: any) => (
                    <Item key={item.ident} textValue={item.title}>
                      <Text marginTop={5}>{item.title}</Text>
                    </Item>
                  )}
                </ComboBox>
              </Flex>

              <Divider size="M" UNSAFE_style={{ marginTop: "5px" }} />

              <Flex
                direction={{ base: "column", L: "row" }}
                gap="size-200"
                marginTop={"size-200"}
              >
                <DatePicker
                  granularity="minute"
                  hideTimeZone
                  width={{ L: "33%", base: "100%" }}
                  isQuiet
                  UNSAFE_className="no-bottom-border-input"
                  label={_t("begins")}
                  labelPosition="side"
                  value={dateFromValue ?? undefined}
                  onChange={(val) => {
                    if (dateFromError) setDateFromError(null);
                    setDateFromValue(val);
                    if (val) {
                      const inThirtyMinutes = val.add({ minutes: 30 });
                      setDateToValue(inThirtyMinutes);
                    }
                  }}
                  validationState={dateFromError ? "invalid" : undefined}
                  errorMessage={dateFromError || undefined}
                />

                <DatePicker
                  granularity="minute"
                  hideTimeZone
                  isQuiet
                  UNSAFE_className="no-bottom-border-input"
                  width={{ L: "33%", base: "100%" }}
                  label={_t("ends")}
                  labelPosition="side"
                  value={dateToValue ?? undefined}
                  onChange={(val) => {
                    if (dateToError) setDateToError(null);
                    setDateToValue(val);
                  }}
                  validationState={dateToError ? "invalid" : undefined}
                  errorMessage={dateToError || undefined}
                />

                <TextField
                  isQuiet
                  UNSAFE_className="no-bottom-border-input"
                  value={formData.location || ""}
                  onChange={(e) => setFormData({ ...formData, location: e })}
                  width={{ base: "100%", L: "33%" }}
                  label={_t("location")}
                  labelPosition="side"
                />
              </Flex>

              <Divider size="M" UNSAFE_style={{ marginTop: "5px" }} />

              <Flex
                direction={{ base: "column", L: "row" }}
                gap="size-100"
                marginTop={"size-200"}
              >
                <Checkbox
                  isSelected={enableReminder}
                  onChange={(e) => {
                    setEnableReminder(e);
                  }}
                >
                  {_t('reminder')}
                </Checkbox>

                {enableReminder && (
                  <ComboBox
                    isQuiet
                    UNSAFE_className="no-bottom-border-input"
                    selectedKey={
                      formData.reminder == null ? "no_reminder" : formData.reminder.toString()
                    }
                    onSelectionChange={(key) => {
                      if (key === "no_reminder") {
                        setFormData({ ...formData, reminder: null });
                      } else {
                        const minutes = parseInt(key!.toString(), 10);
                        setFormData({
                          ...formData,
                          reminder: isNaN(minutes) ? null : minutes
                        });
                      }
                    }}
                    items={[{ key: "no_reminder", minutes: null }, ...BASE_REMINDER_ITEMS]}
                  >
                    {(item: any) => (
                      <Item key={item.key}>
                        {item.minutes == null ? "No reminder" : formatReminderLabel(item.minutes)}
                      </Item>
                    )}
                  </ComboBox>
                )}

                <Checkbox
                  isSelected={enableRecurrence}
                  onChange={(e) => {
                    setEnableRecurrence(e);
                    setFormData({ ...formData, isRepeat: e });
                  }}
                >
                  {_t('recurrence')}
                </Checkbox>

                {enableRecurrence && (
                  <ActionButton
                    isDisabled={!formData.isRepeat}
                    isQuiet
                    UNSAFE_style={{ marginTop: "0px", padding: "10px" }}
                    onPress={() => setShowRecurrence(true)}
                  >
                    {_t('edit')}
                  </ActionButton>
                )}

                <Checkbox
                  isSelected={formData.isPrivate}
                  onChange={(e) => setFormData({ ...formData, isPrivate: e })}
                >
                  {_t('private')}
                </Checkbox>

                <Checkbox
                  isSelected={formData.isChargeable}
                  onChange={(e) => setFormData({ ...formData, isChargeable: e })}
                >
                  {_t('chargeable')}
                </Checkbox>
              </Flex>

              <Divider size="M" UNSAFE_style={{ marginTop: "5px" }} />

              <View marginTop={16} UNSAFE_className={styles.status_container}>
                {/* <Text UNSAFE_style={{ fontWeight: 400 }}>Status *</Text> */}
                <Flex
                  wrap={"nowrap"}
                  alignItems="center"
                  gap="size-100"
                  marginTop={8}
                  marginBottom={8}
                  UNSAFE_className="status-toggles"
                  aria-invalid={!!statusError}
                >
                  {statusItems.map((s: any, i: number) => {
                    const isSelected = formData.solutionStatus === s.ident;
                    const isSecondLast = i === statusItems.length - 2;
                    return (
                      <React.Fragment key={s.ident}>
                        <ToggleButton
                          isSelected={isSelected}
                          onChange={() => {
                            if (statusError) setStatusError(null);
                            setFormData({ ...formData, solutionStatus: s.ident });
                          }}
                          UNSAFE_className={
                            isSelected ? styles.status_button_selected : styles.status_button
                          }
                        >
                          <Flex alignItems="center" gap={2}>
                            <View
                              UNSAFE_className={styles.dot}
                              UNSAFE_style={{ backgroundColor: s.color }}
                            />
                            <Text>{s.title}</Text>
                          </Flex>
                        </ToggleButton>
                        {i < statusItems.length - 2 && <ArrowRightLongIcon size={18} />}
                        {isSecondLast && <ArrowLeftLongIcon size={18} />}
                      </React.Fragment>
                    );
                  })}
                </Flex>
                {statusError && (
                  <Text UNSAFE_style={{ color: "#E34850", marginTop: 4 }}>{statusError}</Text>
                )}
              </View>

              {/* ✅ USERS SECTION (auto-add on selection, no Add button) */}
              <View marginTop={16}>
                <Text UNSAFE_style={{ fontWeight: 400 }}>{_t("users")}</Text>

                <Flex
                  direction={{ base: "column", L: "row" }}
                  position={"relative"}
                  width={"100%"}
                  height={"100%"}
                  alignItems={"start"}
                  justifyContent={"start"}
                  zIndex={0}
                  flex={1}
                  minHeight={"200px"}
                  gap={"size-100"}
                  gridRow={"20"}
                >
                  <Flex
                    minWidth={{ base: "100%", L: "40%" }}
                    direction="column"
                    gap={8}
                    flex
                    UNSAFE_className={styles.user_container}
                  >
                    {/* picker */}
                    <Flex direction="row" gap={8} alignItems="end" width="100%">
                      <ComboBox
                        labelPosition="top"
                        width="100%"
                        isDisabled={availableUsers.length === 0}
                        selectedKey={userPickerKey ?? undefined} // ✅ always filled
                        onSelectionChange={(key) => {
                          const id = key?.toString();
                          if (!id) return;

                          // ✅ auto-add immediately
                          addUserById(id);

                          // clear selection (effect will auto-fill with next)
                          setUserPickerKey(null);
                        }}
                        items={availableUsers}
                      >
                        {(item: any) => (
                          <Item key={String(item.id)} textValue={item.name}>
                            <Text marginTop={5}>{item.name}</Text>
                          </Item>
                        )}
                      </ComboBox>
                    </Flex>

                    {/* list */}
                    <Flex direction="column" gap="size-100" marginTop={8}>
                      {selectedUsers.map((u) => (
                        <Flex
                          key={u.id}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={8}
                        >
                          <Text>{u.name}</Text>

                          <Flex direction="row" gap={8} alignItems="center">
                            {/* head star */}
                            <ActionButton
                              isQuiet
                              aria-label={`Set ${u.name} as head`}
                              onPress={() => handleSetHead(u.id)}
                            >
                              {u.isHead ? (
                                <Star size={18} color="#FFD700" />
                              ) : (
                                <Star size={18} color="#B0BEC5" />
                              )}
                            </ActionButton>

                            {/* remove */}
                            <ActionButton
                              aria-label={`Remove ${u.name}`}
                              isQuiet
                              onPress={() => handleRemoveSelectedUser(u.id)}
                              UNSAFE_style={{ minWidth: 32, height: 32 }}
                            >
                              <CloseIcon size={16} />
                            </ActionButton>
                          </Flex>
                        </Flex>
                      ))}
                    </Flex>
                  </Flex>

                  <TinyMCEEditor
                    key={"tiny_editor_mobile"}
                    initialValue={formData.description || ""}
                    onChange={(e: any) => {
                      setFormData({ ...formData, description: e });
                    }}
                    height={"200px"}
                  />
                </Flex>
              </View>
            </View>

            <Flex
              UNSAFE_style={{
                backgroundColor: "white",
                justifyContent: "end",
                width: "100%",
                marginTop: "10px",
                position: "sticky",
                bottom: 0,
                zIndex: 1
              }}
            >
              <Flex gap={"size-200"} UNSAFE_className={styles.button_container}>
                <ProgressCircle
                  aria-label="....Loading"
                  isHidden={!showLoader}
                  isIndeterminate
                  UNSAFE_style={{ marginRight: 20 }}
                />
                <Button
                  variant="danger"
                  onClick={onClose}
                  className={`${styles.btn} ${styles.btn_cancel}`}
                >
                  {_tt("button_cancel")}
                </Button>
                <Button
                  type="submit"
                  onClick={() => onSubmit()}
                  className={`${styles.btn} ${styles["btn-add"]}`}
                >
                  {isEdit ? _tt("button_update") : _tt("button_save")}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Content>
      </Dialog>

      <DialogContainer onDismiss={() => setShowRecurrence(false)}>
        {showRecurrence && (
          <AddUpdateRecurringEvent
            isOpen={showRecurrence}
            onClose={() => setShowRecurrence(false)}
            repeat={repeat}
            onSave={(newRepeat) => {
              setIsMultiEvent("1");
              setRepeat(newRepeat);
              setFormData((prev: any) => ({
                ...prev,
                isRepeat: true,
                repeat: newRepeat
              }));
            }}
            timeFrom={getTimeFromDateValue(dateFromValue)}
            timeTo={getTimeFromDateValue(dateToValue)}
          />
        )}
      </DialogContainer>
    </>
  );
};

export default AddUpdateEvent;
